import * as React from 'react';

import { ConfidentialityBanner } from '@/components/confidentiality-banner';
import { Badge } from '@/components/ui/badge';
import { Kpi } from '@/components/ui/kpi';
import type { FamilyControlSummary } from '@/routes/supplier/rate-collection/saisie-model';

// =============================================================================
// control-summary.tsx — Récapitulatif de CONTRÔLE de la saisie (SF2/SF3/SF4, DBU §3.5).
// =============================================================================
// Composant READ-ONLY, PUR de présentation : rend, par famille, la complétude (X/Y profils
// saisis) et le NOMBRE DE TAUX RENSEIGNÉS par rapport au total disponible ; il met en évidence
// les profils NON renseignés. Réutilisé tel quel dans la modale de soumission (SF3). Aucune règle
// métier, aucune requête : il reçoit des `FamilyControlSummary` déjà calculés.
//
// G17 (relevé Gate Human 2026-09-15) : la fonctionnalité de CONTRÔLE n'affiche PLUS la moyenne ni la
// médiane — uniquement le nombre de taux renseignés / total. La moyenne et la médiane sont désormais
// portées EN BOUT DE CHAQUE LIGNE de la grille de saisie (G16) et par la tuile de synthèse latérale.
//
// CONFIDENTIALITÉ (SF4) : ce décompte porte UNIQUEMENT les valeurs saisies par CE fournisseur et ne
// quitte JAMAIS le navigateur (cloisonnement / K-anonymity). Il aide la relecture ; il n'est pas transmis.
//
// WCAG 2.1 AA : structure en <section>/<table> sémantique, complétude et profils vides portés par
// un LIBELLÉ TEXTE (jamais la couleur seule ; le badge n'est qu'un renfort), nombres en tabular-nums.

export interface ControlSummaryProps {
  readonly summaries: readonly FamilyControlSummary[];
  /** Titre optionnel (ex. contexte modale de soumission). */
  readonly heading?: string;
}

export function ControlSummary({
  summaries,
  heading = 'Contrôle de la saisie',
}: ControlSummaryProps): React.JSX.Element {
  const headingId = React.useId();
  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <h2 id={headingId} className="text-base font-semibold text-foreground">
        {heading}
      </h2>
      {summaries.length === 0 ? (
        <p role="status" className="text-sm text-muted-foreground">
          Aucun profil à contrôler.
        </p>
      ) : (
        <div className="space-y-4">
          {/* SF4 (DBU §3.5) — cloisonnement RENDU VISIBLE (bannière DS teal) : ces agrégats sont
              calculés dans le navigateur à partir de vos seules saisies et ne quittent jamais le
              poste (K-anonymity / séparation des mondes). La mention sr-only par famille est
              conservée en complément (aucune information retirée). */}
          <ConfidentialityBanner>
            Ces totaux sont cloisonnés&nbsp;: calculés dans votre navigateur à partir de vos
            seules saisies, ils ne sont jamais transmis à un tiers ni visibles d’un autre
            fournisseur.
          </ConfidentialityBanner>
          {summaries.map((summary) => (
            <FamilyBlock
              key={summary.family ?? '__sans_famille__'}
              summary={summary}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function FamilyBlock({
  summary,
}: {
  readonly summary: FamilyControlSummary;
}): React.JSX.Element {
  const { familyLabel, cellProgress, progress, emptyProfiles, partialProfiles } = summary;
  const complete = progress.entered === progress.total;
  // Le participe « saisi » s'accorde avec le NOM « profils » (total), pas avec la part saisie :
  // « 1 / 2 profils saisis », « 1 / 1 profil saisi ». (Accord grammatical, jamais couleur seule.)
  const progressLabel = `${progress.entered} / ${progress.total} profil${
    progress.total > 1 ? 's' : ''
  } saisi${progress.total > 1 ? 's' : ''}`;
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-foreground">{familyLabel}</h3>
        {/* Complétude portée par le LIBELLÉ (le badge n'est qu'un renfort visuel). */}
        <Badge variant={complete ? 'default' : 'outline'}>{progressLabel}</Badge>
      </div>

      {/* Décompte de contrôle (SF4) présenté en KPI du DS (libellé mono + valeur tabular-nums).
          Contexte porté par une description sr-only (jamais couleur seule). */}
      <p className="sr-only">
        Contrôle de la famille {familyLabel} : nombre de taux renseignés par rapport au
        total disponible.
      </p>
      {/* G17 (relevé Gate Human 2026-09-15) : le CONTRÔLE n'affiche QUE « Taux renseignés » (X / Y),
          sans moyenne ni médiane (retirées ; portées désormais en bout de ligne, G16, et par la tuile
          de synthèse). « Somme » avait déjà été retirée (BUG-2, 2026-09-14). */}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi
          label="Taux renseignés"
          value={`${cellProgress.entered} / ${cellProgress.total}`}
        />
      </div>

      {emptyProfiles.length > 0 && (
        <div className="mt-3">
          <p className="text-sm font-medium text-foreground">
            Profils sans aucun taux ({emptyProfiles.length})
          </p>
          <ul className="mt-1 flex flex-wrap gap-2">
            {emptyProfiles.map((p) => (
              <li key={p.code}>
                <Badge variant="outline" className="font-normal">
                  {p.label}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* #7 (DBU l.2876) : profils PARTIELLEMENT complétés — mise en évidence distincte des vides. Le sens
          est porté par le LIBELLÉ TEXTE (jamais la couleur seule) ; le badge n'est qu'un renfort. */}
      {partialProfiles.length > 0 && (
        <div className="mt-3">
          <p className="text-sm font-medium text-foreground">
            Profils partiellement complétés ({partialProfiles.length})
          </p>
          <ul className="mt-1 flex flex-wrap gap-2">
            {partialProfiles.map((p) => (
              <li key={p.code}>
                <Badge variant="secondary" className="font-normal">
                  {p.label}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
